/**
 * FloatingThemeWidget.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { fmt } from '../i18n/format';
import './FloatingThemeWidget.css';

const FloatingThemeWidget = () => {
  const { currentTheme, changeTheme, availableThemes } = useTheme();
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState(null); // Will be calculated on mount
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [justDragged, setJustDragged] = useState(false);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const widgetRef = useRef(null);
  const isDraggingRef = useRef(false);

  // Load position from localStorage on mount, or set default bottom-left position
  useEffect(() => {
    const isMobile = window.innerWidth <= 768;
    const storageKey = isMobile ? 'theme-widget-position-mobile' : 'theme-widget-position';
    const defaultX = isMobile ? 15 : 20;
    const widgetHeight = 60;
    const defaultPos = { x: defaultX, y: window.innerHeight - widgetHeight - 20 };
    const savedPosition = localStorage.getItem(storageKey);
    if (savedPosition) {
      try {
        const pos = JSON.parse(savedPosition);
        // Validate position is within current viewport bounds
        const widgetWidth = 200;
        const maxX = window.innerWidth - widgetWidth;
        const maxY = window.innerHeight - widgetHeight;
        if (pos.x >= 0 && pos.x <= maxX && pos.y >= 0 && pos.y <= maxY) {
          setPosition(pos);
        } else {
          setPosition(defaultPos);
        }
      } catch (e) {
        setPosition(defaultPos);
      }
    } else {
      setPosition(defaultPos);
    }
  }, []);

  // Adjust position on mount if widgetRef is available (for accurate height calculation)
  useEffect(() => {
    const isMobile = window.innerWidth <= 768;
    const storageKey = isMobile ? 'theme-widget-position-mobile' : 'theme-widget-position';
    if (position !== null && widgetRef.current && !localStorage.getItem(storageKey)) {
      // Only adjust if we just set the default (no saved position)
      const widgetHeight = widgetRef.current.offsetHeight || 60;
      setPosition({
        x: isMobile ? 15 : 20,
        y: window.innerHeight - widgetHeight - 20,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount; position filled by previous effect

  // Save position to localStorage whenever it changes
  useEffect(() => {
    if (position !== null) {
      const isMobile = window.innerWidth <= 768;
      const storageKey = isMobile ? 'theme-widget-position-mobile' : 'theme-widget-position';
      localStorage.setItem(storageKey, JSON.stringify(position));
    }
  }, [position]);

  // Handle window resize to constrain position
  useEffect(() => {
    const handleResize = () => {
      if (widgetRef.current) {
        const widgetWidth = widgetRef.current.offsetWidth || 200;
        const widgetHeight = widgetRef.current.offsetHeight || 60;
        const maxX = window.innerWidth - widgetWidth;
        const maxY = window.innerHeight - widgetHeight;

        setPosition(prev => ({
          x: Math.max(0, Math.min(prev.x, maxX)),
          y: Math.max(0, Math.min(prev.y, maxY)),
        }));
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleWidget = () => {
    if (!isDragging && !justDragged) {
      setIsOpen(!isOpen);
    }
    setJustDragged(false);
  };

  const handleThemeChange = (themeId) => {
    changeTheme(themeId);
    // Auto-close after selection (optional)
    setTimeout(() => setIsOpen(false), 300);
  };

  const getCurrentThemeName = () => {
    const theme = availableThemes.find(t => t.id === currentTheme);
    return theme ? theme.name : 'Theme';
  };

  const handleMouseDown = (e) => {
    if (e.button !== 0) return; // Only handle left mouse button
    // Set mouse down state to enable move tracking
    setIsMouseDown(true);
    const rect = widgetRef.current?.getBoundingClientRect();
    if (rect) {
      // Calculate the offset from the mouse position to the widget's top-left corner
      setDragStart({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    }
    // Store the initial click position to detect dragging
    isDraggingRef.current = false;
    e.preventDefault();
  };

  const handleMouseMove = useCallback((e) => {
    // Start dragging only after mouse has moved a bit (to distinguish from clicks)
    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;
    
    // Only start dragging if we've moved more than 5 pixels from the initial position
    if (!isDraggingRef.current && position) {
      const deltaX = Math.abs(newX - position.x);
      const deltaY = Math.abs(newY - position.y);
      if (deltaX > 5 || deltaY > 5) {
        isDraggingRef.current = true;
        setIsDragging(true);
      } else {
        return; // Not dragging yet, don't update position
      }
    }

    if (!isDraggingRef.current) return;

    // Constrain to viewport bounds
    const widgetWidth = widgetRef.current?.offsetWidth || 200;
    const widgetHeight = widgetRef.current?.offsetHeight || 60;
    const maxX = window.innerWidth - widgetWidth;
    const maxY = window.innerHeight - widgetHeight;

    setPosition({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY)),
    });
  }, [dragStart, position]);

  const handleMouseUp = useCallback(() => {
    setIsMouseDown(false);
    if (isDraggingRef.current) {
      setIsDragging(false);
      isDraggingRef.current = false;
      setJustDragged(true);
      // Small delay to prevent click event from firing after drag
      setTimeout(() => setJustDragged(false), 100);
    }
  }, []);

  // Add global event listeners for dragging
  useEffect(() => {
    isDraggingRef.current = isDragging;
    if (isMouseDown) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      if (isDragging) {
        document.body.style.userSelect = 'none'; // Prevent text selection while dragging
      }

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.userSelect = '';
      };
    }
  }, [isMouseDown, isDragging, handleMouseMove, handleMouseUp]);

  // Use position or default bottom-left
  const isMobile = window.innerWidth <= 768;
  const currentPosition = position || { x: isMobile ? 15 : 20, y: window.innerHeight - 80 };
  
  // Determine if widget is in bottom half of screen to adjust panel position
  const isInBottomHalf = currentPosition.y > window.innerHeight / 2;

  return (
    <div 
      ref={widgetRef}
      className={`floating-theme-widget ${isOpen ? 'open' : ''} ${isDragging ? 'dragging' : ''} ${isInBottomHalf ? 'bottom-position' : 'top-position'}`}
      style={{
        left: `${currentPosition.x}px`,
        bottom: 'auto',
        top: `${currentPosition.y}px`,
        right: 'auto',
      }}
    >
      <div className="theme-widget-wrapper">
        {/* Theme Options Panel */}
        {isOpen && (
          <div className="theme-options-panel">
            <div className="theme-options-header">
              <span className="theme-icon">🎨</span>
              <h3>{t.ui.theme.choose}</h3>
            </div>
            <div className="theme-options-list">
              {availableThemes.map(theme => (
                <button
                  key={theme.id}
                  className={`theme-option ${currentTheme === theme.id ? 'active' : ''}`}
                  onClick={() => handleThemeChange(theme.id)}
                  aria-label={fmt(t.ui.theme.selectNamed, { name: theme.name })}
                >
                  <div className="theme-option-colors">
                    <span 
                      className="color-dot" 
                      style={{ backgroundColor: theme.colors['--color-background'] }}
                    />
                    <span 
                      className="color-dot" 
                      style={{ backgroundColor: theme.colors['--color-primary'] }}
                    />
                    <span 
                      className="color-dot" 
                      style={{ backgroundColor: theme.colors['--color-accent'] }}
                    />
                  </div>
                  <span className="theme-option-name">{theme.name}</span>
                  {currentTheme === theme.id && (
                    <span className="theme-check">✓</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Floating Toggle Button */}
        <button
          className="theme-toggle-button"
          onClick={toggleWidget}
          onMouseDown={isMobile ? undefined : handleMouseDown}
          aria-label={t.ui.theme.toggle}
          title={
            isOpen
              ? t.ui.theme.closePanel
              : isMobile
                ? t.ui.theme.change
                : t.ui.theme.changeDrag
          }
          style={{ cursor: isMobile ? 'pointer' : isDragging ? 'grabbing' : 'grab' }}
        >
          <span className="toggle-icon">🎨</span>
        </button>
        <span className="theme-floating-label" aria-hidden="true">{t.ui.theme.floatingLabel}</span>
      </div>
    </div>
  );
};

export default FloatingThemeWidget;

